import { useNavigate, useParams } from "@tanstack/solid-router";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createMemo, createSignal } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { EventPageHeader } from "~/components/EventPageHeader";
import { FOLLOW_UP_OPTIONS, SponsorLeadCapture } from "~/components/SponsorLeadCapture";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import {
  fetchConnectEvents,
  fetchProfile,
  fetchScanQuota,
  fetchSponsorLeads,
  hasClaimedTicket,
  type FollowUpAction,
  type SponsorLeadListItem,
  type SponsorLeadScope,
} from "~/lib/api";
import {
  buildSponsorLeadsCsv,
  downloadTextFile,
  sponsorLeadsCsvFilename,
} from "~/lib/sponsor-leads-csv";

function followUpLabel(id: FollowUpAction | null | undefined): string | null {
  if (!id) return null;
  return FOLLOW_UP_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

function formatScannedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function SponsorLeadsPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventId = () => params().eventId ?? "";

  const [scope, setScope] = createSignal<SponsorLeadScope>("mine");
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [exporting, setExporting] = createSignal(false);

  const events = createQuery(() => ({ queryKey: ["events"], queryFn: fetchConnectEvents }));
  const profileQuery = createQuery(() => ({
    queryKey: ["profile", eventId()],
    queryFn: () => fetchProfile(eventId()),
    enabled: !!eventId(),
  }));
  const quotaQuery = createQuery(() => ({
    queryKey: ["scan-quota", eventId()],
    queryFn: () => fetchScanQuota(eventId()),
    enabled: !!eventId() && hasClaimedTicket(profileQuery.data?.data),
  }));
  const leadsQuery = createQuery(() => ({
    queryKey: ["sponsor-leads", eventId(), scope()],
    queryFn: () => fetchSponsorLeads(eventId(), scope()),
    enabled: !!eventId() && quotaQuery.data?.data.is_sponsor === true,
  }));

  const needsTicket = () =>
    profileQuery.isSuccess && !hasClaimedTicket(profileQuery.data?.data ?? null);

  const eventTitle = () => {
    const match = events.data?.data.find((e) => e.ticketing_event_id === eventId());
    if (!match) return eventId();
    if (match.city) return `${match.city} 2026`;
    return match.name;
  };

  const canTeam = createMemo(() => {
    const org = quotaQuery.data?.data.sponsor_org ?? leadsQuery.data?.data.sponsor_org;
    return !!(org && org.trim());
  });

  const items = () => leadsQuery.data?.data.items ?? [];
  const sponsorOrg = () =>
    quotaQuery.data?.data.sponsor_org ?? leadsQuery.data?.data.sponsor_org ?? null;
  const canDownload = () => leadsQuery.isSuccess && items().length > 0 && !exporting();

  function openLead(item: SponsorLeadListItem) {
    setSelectedId((prev) => (prev === item.scan_id ? null : item.scan_id));
  }

  function onLeadSaved() {
    void queryClient.invalidateQueries({ queryKey: ["sponsor-leads", eventId()] });
  }

  function downloadCsv() {
    const rows = items();
    if (!rows.length || exporting()) return;
    setExporting(true);
    try {
      const csv = buildSponsorLeadsCsv(rows);
      const filename = sponsorLeadsCsvFilename({
        sponsorOrg: sponsorOrg(),
        eventTitle: eventTitle(),
        scope: scope(),
      });
      downloadTextFile(filename, csv);
    } finally {
      window.setTimeout(() => setExporting(false), 400);
    }
  }

  return (
    <section class="page page--flush">
      <Show when={needsTicket()}>
        <div style={{ padding: "20px" }}>
          <TicketClaimGate eventId={eventId()} cityLabel={eventTitle()} />
        </div>
      </Show>

      <Show when={!needsTicket()}>
        <EventPageHeader
          eventTitle={eventTitle()}
          label="Leads"
          trailing={
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() =>
                navigate({ to: "/directory/$eventId/scan", params: { eventId: eventId() } })
              }
            >
              Scan
            </button>
          }
        />

        <div class="page-title-block">
          <h1>Sponsor leads</h1>
          <p class="muted">
            CRM notes from badge scans
            <Show when={quotaQuery.data?.data.sponsor_org} keyed>
              {(org) => <> · {org}</>}
            </Show>
          </p>
        </div>

        <Show when={quotaQuery.isSuccess && !quotaQuery.data?.data.is_sponsor}>
          <div class="error-box" style={{ margin: "0 20px" }}>
            Leads are only available to sponsor reps for this event.
          </div>
        </Show>

        <Show when={quotaQuery.data?.data.is_sponsor}>
          <div class="leads-toolbar">
            <Show when={canTeam()} fallback={<div />}>
              <div class="leads-scope-toggle" role="group" aria-label="Lead list scope">
                <button
                  type="button"
                  class={`btn btn-sm${scope() === "mine" ? " btn-primary" : " btn-outline"}`}
                  aria-pressed={scope() === "mine"}
                  onClick={() => {
                    setScope("mine");
                    setSelectedId(null);
                  }}
                >
                  My leads
                </button>
                <button
                  type="button"
                  class={`btn btn-sm${scope() === "team" ? " btn-primary" : " btn-outline"}`}
                  aria-pressed={scope() === "team"}
                  onClick={() => {
                    setScope("team");
                    setSelectedId(null);
                  }}
                >
                  Team
                </button>
              </div>
            </Show>
            <button
              type="button"
              class={`btn btn-sm leads-download-btn${exporting() ? " btn-primary" : " btn-outline"}`}
              disabled={!canDownload() && !exporting()}
              aria-busy={exporting()}
              onClick={downloadCsv}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 4v12m0 0l-4-4m4 4l4-4M5 19h14"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {exporting() ? "Preparing…" : "Download"}
            </button>
          </div>
          <p class="muted leads-export-hint">
            <Show
              when={items().length > 0}
              fallback="CSV export unlocks when you have leads"
            >
              Exports the current list as CSV · {items().length}{" "}
              {items().length === 1 ? "lead" : "leads"}
            </Show>
          </p>

          <Show when={leadsQuery.isLoading}>
            <p class="muted" style={{ padding: "0 20px" }}>
              Loading leads…
            </p>
          </Show>
          <Show when={leadsQuery.isError}>
            <div class="error-box" style={{ margin: "0 20px" }}>
              {(leadsQuery.error as Error).message}
            </div>
          </Show>

          <Show when={leadsQuery.isSuccess && items().length === 0}>
            <p class="muted" style={{ padding: "0 20px" }}>
              No leads yet — scan a badge from the Scan tab to capture one.
            </p>
          </Show>

          <Show when={items().length}>
            <ul class="connection-list leads-list">
              <For each={items()}>
                {(item) => (
                  <li class="connection-row connection-row--incoming">
                    <button type="button" class="connection-peer" onClick={() => openLead(item)}>
                      <Avatar
                        url={item.display.avatar_url}
                        name={item.display.display_name}
                        size="sm"
                      />
                      <div>
                        <strong>{item.display.display_name ?? "Attendee"}</strong>
                        <span class="muted">
                          {item.display.company ?? item.display.job_role ?? item.display.headline}
                        </span>
                        <div class="leads-meta">
                          <Show when={item.sponsor_lead.rating != null}>
                            <span class="status-chip">{"★".repeat(item.sponsor_lead.rating!)}</span>
                          </Show>
                          <Show when={followUpLabel(item.sponsor_lead.follow_up)} keyed>
                            {(label) => <span class="status-chip">{label}</span>}
                          </Show>
                          <span class="muted">{formatScannedAt(item.scanned_at)}</span>
                        </div>
                        <Show when={scope() === "team" && item.scanner_name}>
                          <span class="muted leads-scanner">Scanned by {item.scanner_name}</span>
                        </Show>
                        <Show when={item.sponsor_lead.note}>
                          <p class="connection-note leads-note-snippet">
                            {item.sponsor_lead.note!.length > 120
                              ? `${item.sponsor_lead.note!.slice(0, 120)}…`
                              : item.sponsor_lead.note}
                          </p>
                        </Show>
                      </div>
                    </button>

                    <Show when={selectedId() === item.scan_id}>
                      <div class="leads-detail">
                        <Show when={item.contact.email}>
                          <p class="muted">
                            Contact:{" "}
                            <a href={`mailto:${item.contact.email}`}>{item.contact.email}</a>
                          </p>
                        </Show>
                        <Show when={!item.contact.email && item.contact.ticket_email_redacted}>
                          <p class="muted">Ticket email: {item.contact.ticket_email_redacted}</p>
                        </Show>
                        <Show when={item.can_edit}>
                          <SponsorLeadCapture
                            scanId={item.scan_id}
                            initialNote={item.sponsor_lead.note ?? ""}
                            initialRating={item.sponsor_lead.rating}
                            initialFollowUp={item.sponsor_lead.follow_up}
                            onSaved={onLeadSaved}
                          />
                        </Show>
                        <Show when={!item.can_edit}>
                          <p class="muted">
                            View only — only the rep who scanned can edit this lead.
                          </p>
                          <Show when={item.sponsor_lead.note}>
                            <p class="profile-detail-bio">{item.sponsor_lead.note}</p>
                          </Show>
                        </Show>
                        <Show when={item.user_id}>
                          <button
                            type="button"
                            class="btn btn-outline btn-sm"
                            onClick={() =>
                              navigate({
                                to: "/directory/$eventId/profile/$profileId",
                                params: { eventId: eventId(), profileId: item.user_id! },
                              })
                            }
                          >
                            View profile
                          </button>
                        </Show>
                      </div>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </Show>
    </section>
  );
}
